"use client"
import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs"
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar"
import { ThumbsUp, ThumbsDown, MessageSquare, Edit, AlertTriangle, Plus, Trash2 } from "lucide-react"
import { Textarea } from "../../ui/textarea"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import useKeyUnknowns from "../../../hooks/useKeyUnknowns"

const KeyUnknownsModule = ({ actionId, action }) => {
  console.log("KeyUnknownsModule rendered with actionId:", actionId)

  const {
    unknowns,
    suggestions,
    comments,
    newSuggestion,
    newComment,
    activeTab,
    loading,
    setActiveTab,
    setNewComment,
    handleAddUnknown,
    handleRemoveUnknown,
    handleUnknownChange,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
  } = useKeyUnknowns(actionId, action)

  console.log("KeyUnknownsModule hook data:", {
    unknownsCount: unknowns.length,
    suggestionsCount: suggestions.length,
    commentsCount: comments.length,
    loading,
  })

  if (loading) {
    return <div className="p-4">Loading key unknowns data...</div>
  }

  return (
    <div>
      <CardHeader className="px-0 pt-0">
        <CardTitle>Key Unknowns</CardTitle>
      </CardHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="current">Current</TabsTrigger>
          <TabsTrigger value="suggestions">Suggestions ({suggestions.length})</TabsTrigger>
          <TabsTrigger value="discussion">Discussion ({comments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="current">
          <Card>
            <CardContent className="p-4">
              {unknowns.length === 0 ? (
                <div className="text-center py-6">
                  <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <p className="text-muted-foreground">No key unknowns have been identified yet.</p>
                  <Button onClick={() => setActiveTab("suggestions")} variant="outline" className="mt-4">
                    <Edit className="mr-2 h-4 w-4" /> Suggest Key Unknowns
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {unknowns.map((item, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="font-medium">{item.unknown}</h3>

                          {item.impact && (
                            <div className="mt-2">
                              <span className="text-sm font-medium">Potential Impact:</span>
                              <p className="text-sm mt-1">{item.impact}</p>
                            </div>
                          )}

                          {item.resolution && (
                            <div className="mt-2">
                              <span className="text-sm font-medium">Proposed Resolution:</span>
                              <p className="text-sm mt-1">{item.resolution}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="mt-4 flex justify-end">
                    <Button onClick={() => setActiveTab("suggestions")} variant="outline">
                      <Edit className="mr-2 h-4 w-4" /> Suggest Edit
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suggestions">
          <Card className="mb-4">
            <CardContent className="p-4">
              <h3 className="text-lg font-medium mb-4">Suggest Key Unknowns</h3>

              <div className="space-y-4">
                {newSuggestion.map((item, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Unknown #{index + 1}</h4>
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveUnknown(index)}
                          className="text-red-500 h-6 w-6 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`unknown-${index}`}>Unknown Factor</Label>
                      <Input
                        id={`unknown-${index}`}
                        value={item.unknown}
                        onChange={(e) => handleUnknownChange(index, "unknown", e.target.value)}
                        placeholder="What is unknown about this action?"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`impact-${index}`}>Potential Impact</Label>
                      <Textarea
                        id={`impact-${index}`}
                        value={item.impact}
                        onChange={(e) => handleUnknownChange(index, "impact", e.target.value)}
                        placeholder="How might this unknown impact the action?"
                        className="min-h-[80px]"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`resolution-${index}`}>Proposed Resolution</Label>
                      <Textarea
                        id={`resolution-${index}`}
                        value={item.resolution}
                        onChange={(e) => handleUnknownChange(index, "resolution", e.target.value)}
                        placeholder="How can this unknown be resolved or mitigated?"
                        className="min-h-[80px]"
                      />
                    </div>
                  </div>
                ))}

                <Button type="button" variant="outline" onClick={handleAddUnknown} className="w-full">
                  <Plus className="h-4 w-4 mr-2" /> Add Another Unknown
                </Button>

                <div className="flex justify-end">
                  <Button onClick={handleSubmitSuggestion}>Submit Suggestion</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {suggestions.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No suggestions yet. Be the first to suggest key unknowns!
            </p>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion) => (
                <Card key={suggestion.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar>
                        <AvatarImage src={suggestion.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{suggestion.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{suggestion.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(suggestion.createdAt).toLocaleString()}
                          </span>
                        </div>

                        <div className="space-y-3 mb-4">
                          {suggestion.unknowns &&
                            suggestion.unknowns.map((item, index) => (
                              <div key={index} className="border rounded-md p-3 bg-gray-50">
                                <h4 className="font-medium">{item.unknown}</h4>

                                {item.impact && (
                                  <div className="mt-2">
                                    <span className="text-xs font-medium">Potential Impact:</span>
                                    <p className="text-xs mt-0.5">{item.impact}</p>
                                  </div>
                                )}

                                {item.resolution && (
                                  <div className="mt-2">
                                    <span className="text-xs font-medium">Proposed Resolution:</span>
                                    <p className="text-xs mt-0.5">{item.resolution}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "up")}
                            className={suggestion.userVote === "up" ? "bg-green-50" : ""}
                          >
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            <span>{suggestion.upvotes || 0}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "down")}
                            className={suggestion.userVote === "down" ? "bg-red-50" : ""}
                          >
                            <ThumbsDown className="h-4 w-4 mr-1" />
                            <span>{suggestion.downvotes || 0}</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="discussion">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                {comments.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No comments yet. Start the discussion!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3 p-3 rounded-lg bg-gray-50">
                      <Avatar>
                        <AvatarImage src={comment.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{comment.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{comment.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-2">
                <Textarea
                  placeholder="Add to the discussion..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmitComment}>
                    <MessageSquare className="mr-2 h-4 w-4" /> Post Comment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default KeyUnknownsModule
